import React, { useEffect, useRef, useState } from "react";

interface OtpModalProps {
  show: boolean;
  phone: string;
  onClose: () => void;
  onVerify: (otp: string) => void;
  onResend: () => void;
}

const OtpModal: React.FC<OtpModalProps> = ({ show, phone, onClose, onVerify, onResend }) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [countdown, setCountdown] = useState<number>(120);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (show) {
      setCountdown(120);
      setOtp(Array(6).fill(""));
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [show]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const formatCountdown = () =>
    `${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`;

  const getOtpValue = () => otp.join("");

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
      <div className="bg-black bg-opacity-60 text-white w-[90%] max-w-md rounded-2xl shadow-xl p-6 relative border border-yellow-400">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-yellow-400 hover:text-red-500 text-xl"
        >
          &times;
        </button>

        <h2 className="text-xl font-bold text-yellow-300 text-center mb-4 flex items-center justify-center gap-2">
          <i className="fas fa-shield-alt text-yellow-400"></i> OTP Verification
        </h2>

        <p className="text-sm text-gray-300 text-center mb-4">
          We've sent a 6-digit code to <strong>+1{phone}</strong>
        </p>

        <div className="flex justify-center gap-2 mb-4">
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputsRef.current[idx] = el)}
              type="text"
              inputMode="numeric"
              className="w-10 h-12 text-center border border-yellow-400 rounded-lg text-lg bg-black text-yellow-200 shadow focus:outline-none focus:ring-2 focus:ring-yellow-400"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            />
          ))}
        </div>

        <div className="flex justify-between items-center text-sm mb-3">
          <span className="text-gray-400">Resend OTP in {formatCountdown()}</span>
          <button
            onClick={onResend}
            className="text-yellow-400 hover:underline disabled:text-gray-500"
            disabled={countdown > 0}
          >
            Resend OTP
          </button>

        </div>
          <span className="text-gray-500 block text-sm m-4">By entring OTP, You agree that i have Voluntarily requested the change to my work schedule indicated above. A manager did not request this of me.</span>

        <button
          onClick={() => onVerify(getOtpValue())}
          className="w-full bg-yellow-500 text-black font-bold py-2 rounded-lg shadow hover:bg-yellow-600 transition"
        >
          <i className="fas fa-check-circle mr-2"></i>Verify & Submit
        </button>
      </div>
    </div>
  );
};

export default OtpModal;
